import React from "react";
import PropTypes from "prop-types";
import { format } from "date-fns";
import { es } from "date-fns/locale";

// Iconos
import VisibilityIcon from "../../../assets/svg/components/see.svg";
import VisibilityLightIcon from "../../../assets/svg/components/see_light.svg";
import EditIcon from "../../../assets/svg/view/edit.svg";
import EditLightIcon from "../../../assets/svg/view/edit_ligh.svg";
import MissingIcon from "../../../assets/svg/view/arrow_up.svg";
import MissingLightIcon from "../../../assets/svg/view/arrow_up.svg";
import '../../../assets/scss/components/ui/cards/VehicleDocumentCard.scss'
const VehicleDocumentCard = ({ 
  document, 
  theme, 
  onPreview, 
  onEdit, 
  status,
  isRequired = false,
  isMissing = false,
  isExtraDocument = false
}) => {
  // Funciones helper
  const formatDate = (dateString) => {
    if (!dateString) return "No especificada";
    try {
      return format(new Date(dateString), "dd/MM/yyyy", { locale: es });
    } catch {
      return "Fecha inválida";
    }
  };

  const getDaysRemaining = (expirationDate) => {
    if (!expirationDate) return null;
    
    const today = new Date();
    const expDate = new Date(expirationDate);
    const daysUntilExpiration = Math.ceil(
      (expDate - today) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiration < 0) {
      return `Vencido hace ${Math.abs(daysUntilExpiration)} días`;
    } else if (daysUntilExpiration === 0) {
      return "Vence hoy";
    } else {
      return `${daysUntilExpiration} días restantes`;
    }
  };

  const getStatusColor = (status) => {
    const statusMap = {
      expired: "expired",
      critical: "critical", 
      warning: "warning",
      valid: "valid",
      empty: "empty",
      "not-required": "not-required",
      "missing-required": "missing-required",
      "missing-optional": "missing-optional"
    };
    return statusMap[status] || "default";
  };

  // Renderizado condicional de iconos
  const renderIcon = (lightIcon, darkIcon, alt) => {
    try {
      const iconSrc = theme === "dark" ? lightIcon : darkIcon;
      return <img src={iconSrc} alt={alt} />;
    } catch (error) {
      console.warn(`Error cargando icono: ${alt}`, error);
      return <span>{alt}</span>;
    }
  };

  return (
    <div className={`vehicle-document-card theme-${theme} status-${getStatusColor(status)} ${isMissing ? 'missing' : ''} ${isExtraDocument ? 'extra-document' : ''}`}>
      <div className="card-header">
        <h4 className="document-title">
          {document.original_name_description || document.document_type_name}
        </h4>
        <div className="badges-container">
          {isRequired && (
            <span className="required-badge">Requerido</span>
          )}
          {isMissing && (
            <span className="missing-badge">Faltante</span>
          )}
          {isExtraDocument && (
            <span className="extra-badge">Extra</span>
          )}
        </div>
      </div>

      <div className="card-content">
        {isMissing ? (
          // Contenido para documentos faltantes
          <div className="missing-content">
            <div className="missing-message">
              <p className="missing-title">Documento no encontrado</p>
            </div>
          </div>
        ) : (
          // Contenido para documentos existentes
          <>
            <div className="date-info">
              <div className="date-row">
                <span className="date-label">Expedición:</span>
                <span className="date-value">
                  {formatDate(document.expedition_date)}
                </span>
              </div>
              <div className="date-row">
                <span className="date-label">Vencimiento:</span>
                <span className="date-value">
                  {formatDate(document.expiration_date)}
                </span>
              </div>
            </div>

            {document.expiration_date && (
              <div className={`days-remaining status-${getStatusColor(status)}`}>
                {getDaysRemaining(document.expiration_date)}
              </div>
            )}

            {!document.file_name && !isMissing && (
              <div className="no-file-message">
                Sin archivo adjunto
              </div>
            )}
          </>
        )}
      </div>

      <div className="card-actions">
        {/* Solo mostrar preview si el documento existe y tiene archivo */}
        {!isMissing && document.file_name && (
          <button
            onClick={onPreview}
            className={`action-btn preview-btn theme-${theme}`}
            title="Ver documento"
          >
            {renderIcon(VisibilityLightIcon, VisibilityIcon, "Ver")}
          </button>
        )}
        
        {/* Botón de editar/agregar siempre presente */}
        <button
          onClick={onEdit}
          className={`action-btn edit-btn theme-${theme} ${isMissing ? 'add-btn' : ''}`}
          title={isMissing ? "Ir al perfil para agregar documento" : "Editar en perfil"}
        >
          {isMissing 
            ? renderIcon(MissingLightIcon, MissingIcon, "Agregar")
            : renderIcon(EditLightIcon, EditIcon, "Editar")
          }
        </button>
      </div>
    </div>
  );
};

// PropTypes con validación exhaustiva
VehicleDocumentCard.propTypes = {
  document: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    original_name_description: PropTypes.string,
    document_type_name: PropTypes.string,
    required: PropTypes.bool,
    expedition_date: PropTypes.string,
    expiration_date: PropTypes.string,
    file_name: PropTypes.string
  }).isRequired,
  theme: PropTypes.oneOf(['light', 'dark']).isRequired,
  onPreview: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  status: PropTypes.string.isRequired,
  isRequired: PropTypes.bool,
  isMissing: PropTypes.bool,
  isExtraDocument: PropTypes.bool
};

export default VehicleDocumentCard;